/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { Col, Row } from 'antd';

import {
  emptyFieldLayout,
  loadAgendaFieldLayout,
} from '../../Pages/Role/FieldCustomization/API/FieldCustomizationApi';

import CustomFieldInput from './CustomFieldInput';
import {
  createShowField,
  isVigenteLayout,
  readLayoutField,
  readLayoutSections,
  readLayoutUnsectioned,
  readLoggedRoleId,
  readPortalResource,
  shouldRenderTaskField,
} from './shouldRenderTaskField';

import './RegisterFieldLayout.scss';

export function readEntityCustomFields(entity) {
  return entity?.customFields ?? entity?.CustomFields ?? {};
}

export function useRegisterFieldLayout(resourceName) {
  const [fieldLayout, setFieldLayout] = useState(emptyFieldLayout);
  const portalResource = readPortalResource(resourceName);
  const roleId = readLoggedRoleId();

  useEffect(() => {
    loadAgendaFieldLayout(portalResource.resourceId).then(setFieldLayout);
  }, [portalResource.resourceId]);

  return { fieldLayout, roleId, resourceId: portalResource.resourceId };
}

function flattenLayoutFields(fieldLayout) {
  const fields = readLayoutUnsectioned(fieldLayout).map(readLayoutField);
  readLayoutSections(fieldLayout).forEach((section) => {
    (section.fields ?? section.Fields ?? []).forEach((item) => {
      fields.push(readLayoutField(item));
    });
  });
  return fields;
}

export function createRegisterShowField(fieldsPermissions, fieldLayout, roleId) {
  const showByCargoAndOverlay = createShowField(fieldsPermissions, fieldLayout, roleId);
  return (fieldName) => {
    if (!showByCargoAndOverlay(fieldName)) {
      return false;
    }

    if (!isVigenteLayout(fieldLayout)) {
      return true;
    }

    const visibleNames = flattenLayoutFields(fieldLayout).map((field) => field.name);
    if (visibleNames.length === 0) {
      return true;
    }

    return visibleNames.includes(fieldName);
  };
}

function renderCustomRegisterField(field, widgetContext) {
  const access = field.accessHint ?? widgetContext.fieldsPermissions?.[field.name]?.access ?? 1;
  return (
    <Col span={24} key={field.name}>
      <CustomFieldInput
        name={['customFields', field.name]}
        label={field.label}
        fieldType={field.fieldType}
        access={access}
        disabled={widgetContext.loading}
        form={widgetContext.form}
        task={widgetContext.form?.getFieldsValue?.() ?? {}}
        handleTaskUpdate={widgetContext.handleTaskUpdate}
        setUploadFileType={widgetContext.setUploadFileType}
        setUploadFileModalOpen={widgetContext.setUploadFileModalOpen}
        isTaskReadOnly={widgetContext.loading}
        isEditing={!widgetContext.loading && access === 2}
      />
    </Col>
  );
}

function renderMappedWidget(field, widgetMap, renderedWidgets) {
  const widget = widgetMap[field.name];
  if (!widget || renderedWidgets.has(widget)) {
    return null;
  }

  renderedWidgets.add(widget);
  return typeof widget === 'function' ? widget() : widget;
}

function RegisterLayoutField({ layoutField, widgetContext, widgetMap, renderedWidgets }) {
  const field = readLayoutField(layoutField);
  if (
    !shouldRenderTaskField({
      fieldName: field.name,
      fieldsPermissions: widgetContext.fieldsPermissions,
      fieldLayout: widgetContext.fieldLayout,
      roleId: widgetContext.roleId,
    })
  ) {
    return null;
  }

  const mapped = renderMappedWidget(field, widgetMap, renderedWidgets);
  if (mapped) {
    return mapped;
  }

  if (!field.fieldType) {
    return null;
  }

  return renderCustomRegisterField(field, widgetContext);
}

function fieldHasRegisterWidget(field, widgetMap) {
  return Boolean(widgetMap[field.name] || field.fieldType);
}

function VisibleRegisterSection({ section, widgetContext, widgetMap, renderedWidgets }) {
  const sectionName = section.name ?? section.Name;
  const sectionFields = (section.fields ?? section.Fields ?? [])
    .map(readLayoutField)
    .filter((field) =>
      shouldRenderTaskField({
        fieldName: field.name,
        fieldsPermissions: widgetContext.fieldsPermissions,
        fieldLayout: widgetContext.fieldLayout,
        roleId: widgetContext.roleId,
      })
    )
    .filter((field) => fieldHasRegisterWidget(field, widgetMap));

  if (sectionFields.length === 0) {
    return null;
  }

  return (
    <Col span={24}>
      <h3 className="register-custom-section-title">{sectionName}</h3>
      {sectionFields.map((field) => (
        <RegisterLayoutField
          key={field.name}
          layoutField={field}
          widgetContext={widgetContext}
          widgetMap={widgetMap}
          renderedWidgets={renderedWidgets}
        />
      ))}
    </Col>
  );
}

export function RegisterVigenteFields({
  fieldLayout,
  fieldsPermissions,
  roleId,
  widgetMap,
  form,
  loading,
}) {
  const renderedWidgets = new Set();
  const widgetContext = {
    fieldLayout,
    fieldsPermissions,
    roleId,
    form,
    loading,
    handleTaskUpdate: async () => {},
    setUploadFileType: () => {},
    setUploadFileModalOpen: () => {},
  };
  const unsectioned = readLayoutUnsectioned(fieldLayout);
  const sections = readLayoutSections(fieldLayout);

  return (
    <>
      {unsectioned.map((layoutField) => (
        <RegisterLayoutField
          key={readLayoutField(layoutField).name}
          layoutField={layoutField}
          widgetContext={widgetContext}
          widgetMap={widgetMap}
          renderedWidgets={renderedWidgets}
        />
      ))}
      {sections.map((section) => (
        <VisibleRegisterSection
          key={section.sectionId ?? section.SectionId ?? section.name}
          section={section}
          widgetContext={widgetContext}
          widgetMap={widgetMap}
          renderedWidgets={renderedWidgets}
        />
      ))}
    </>
  );
}

export function RegisterLayoutBody({
  fieldLayout,
  fieldsPermissions,
  roleId,
  widgetMap,
  form,
  loading,
  padraoFields,
  extraFields,
}) {
  if (!isVigenteLayout(fieldLayout)) {
    return padraoFields;
  }

  const vigenteFields = (
    <RegisterVigenteFields
      fieldLayout={fieldLayout}
      fieldsPermissions={fieldsPermissions}
      roleId={roleId}
      widgetMap={widgetMap ?? {}}
      form={form}
      loading={loading}
    />
  );

  if (!extraFields) {
    return vigenteFields;
  }

  return (
    <>
      <Row gutter={[24]}>{vigenteFields}</Row>
      {extraFields}
    </>
  );
}

export function applyRegisterCustomFields(entity) {
  if (!entity) {
    return entity;
  }

  return {
    ...entity,
    customFields: readEntityCustomFields(entity),
  };
}

export { flattenLayoutFields, isVigenteLayout };
